import React, { useState } from 'react';

// Import CSS styles
import './styles/ContentPageTemplate.css';

// Accordion component for FAQ-style content
const Accordion: React.FC<{
  title: string;
  children: React.ReactNode;
  initiallyOpen?: boolean;
}> = ({ title, children, initiallyOpen = false }) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  
  return (
    <div className="accordion-item">
      <div 
        className={`accordion-header ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {title}
      </div>
      <div className={`accordion-content ${isOpen ? 'open' : ''}`}>
        {children}
      </div>
    </div>
  );
};

// Helper function to parse HTML content and extract headings for FAQ-style display
const parseContentForAccordion = (content: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const headings = Array.from(doc.querySelectorAll('h2, h3'));
  
  const faqItems = headings.map(heading => {
    const title = heading.textContent || '';
    let content = '';
    let currentElement = heading.nextElementSibling;
    
    while (currentElement && !['H2', 'H3'].includes(currentElement.tagName)) {
      content += currentElement.outerHTML;
      currentElement = currentElement.nextElementSibling;
    }
    
    return { title, content };
  });
  
  return faqItems;
};

// Main content page template component
interface ContentPageTemplateProps {
  title: string;
  content: string;
  useFaqStyle?: boolean;
}

const ContentPageTemplate: React.FC<ContentPageTemplateProps> = ({
  title,
  content,
  useFaqStyle = false
}) => {
  // If using FAQ style, parse the content for accordion items
  const faqItems = useFaqStyle ? parseContentForAccordion(content) : [];
  
  return (
    <div className="content-page-container">
      <div className="content-page-header">
        <h1 className="content-page-title">{title}</h1>
      </div>
      
      <div className="content-page-content">
        {useFaqStyle && faqItems.length > 0 ? (
          <div className="accordion-container">
            {faqItems.map((item, index) => (
              <Accordion 
                key={index} 
                title={item.title} 
                initiallyOpen={index === 0}
              >
                <div dangerouslySetInnerHTML={{ __html: item.content }} />
              </Accordion>
            ))}
          </div>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        )}
      </div>
    </div>
  );
};

export default ContentPageTemplate;
